import { useRef } from 'react';
import { useAtomValue } from 'jotai';
import { cva } from 'class-variance-authority';
import { cn } from '@rocco/utils/cn';
import { ReactComponent as ArrowRightIcon } from '@rocco/icons/svg/arrow/arrow-right.svg';
import { ReactComponent as PlayIcon } from '@rocco/icons/svg/play.svg';
import { Button } from '@rocco/components/button';
import type { ArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import { TileMedia } from '@rocco/ui/tile-shared/components/TileMedia';
import { TileBadge } from '@rocco/ui/tile-shared/components/TileBadge';
import {
    formatDateToDDMMYY,
    formatDateToDDMonthYYYY,
} from '@rocco/utils/format-date';
import { TileFollowButton } from '@rocco/ui/tile-shared/components/TileFollowButton';
import { trackTileMediaDetailClick } from '@archipro-website/web/app/modules/tracking/util/trackTileMedia';
import { useTracker } from '../../../../../tracker/src/react-bindings/context';
import { OptionalLink } from '@rocco/components/link/OptionalLink';
import { TILE_COMPONENTS_CLASS_NAMES } from '@rocco/ui/tile-shared/utils/contstants';
import { uiContextAtomTileActions } from '@rocco/ui/tile-shared/hooks/use-shared-tiles';

const _articleTileContentVariants = cva('', {
    variants: {
        variant: {
            featured:
                'absolute inset-x-0 bottom-0 flex items-end justify-between gap-18 px-[1.83rem] pb-[2.12rem] text-gray-50',
            default: 'pt-4 text-[#515151]',
            // 10px image-to-text gap, 30px right inset (Figma 49:4497).
            editorial: 'pt-[0.625rem] pr-[1.875rem] text-[#1f1f21]',
            // Directory card: 30px image-to-text gap, 38.5px right inset
            // (Figma 60:2077).
            editorialLarge: 'pt-[1.875rem] pr-[2.4066rem] text-[#1f1f21]',
        },
    },
    defaultVariants: {
        variant: 'featured',
    },
});

const _articleTileTitleVariants = cva(
    'text-base font-medium tracking-[0.02rem]',
    {
        variants: {
            variant: {
                featured: 'pr-[5rem]',
                default: 'line-clamp-3 text-gray-900',
                // Gestura Headline 24/32, -0.24px, capitalised (Figma 49:4497).
                editorial:
                    'line-clamp-3 font-gestura text-[1.5rem] leading-[2rem] font-normal tracking-[-0.015rem] text-[#1f1f21] capitalize',
                editorialLarge:
                    'line-clamp-3 font-gestura text-[1.5rem] leading-[2rem] font-normal tracking-[-0.015rem] text-[#1f1f21] capitalize',
            },
        },
        defaultVariants: {
            variant: 'featured',
        },
    },
);

const _articleTileMetaVariants = cva(
    'line-clamp-1 text-3.5 leading-[1.1375rem] font-normal tracking-[0.0175rem] text-ellipsis md:text-[0.875rem]',
    {
        variants: {
            variant: {
                featured: '',
                default: '',
                // Neue Haas Text 12/18, +0.96px, uppercase (Figma 49:4497).
                editorial:
                    'text-[0.75rem] leading-[1.125rem] tracking-[0.06rem] text-[#1f1f21] uppercase md:text-[0.75rem]',
                editorialLarge:
                    'text-[0.75rem] leading-[1.125rem] tracking-[0.06rem] text-[#1f1f21] uppercase md:text-[0.75rem]',
            },
        },
        defaultVariants: {
            variant: 'featured',
        },
    },
);

const _articleTileTextStackVariants = cva('flex flex-col', {
    variants: {
        variant: {
            featured: 'gap-3 md:gap-2.25',
            default: 'gap-3 md:gap-2.25',
            // 20px title-to-meta gap (Figma 49:4497).
            editorial: 'gap-[1.25rem]',
            editorialLarge: 'gap-[1.25rem]',
        },
    },
    defaultVariants: {
        variant: 'featured',
    },
});

/**
 * The frame wrapping the media and the content block. `editorial` becomes a
 * flex column so the image takes the space the title leaves, which is what
 * keeps every card in a row the same height and their meta lines aligned.
 */
const _articleTileFrameVariants = cva('group relative h-full', {
    variants: {
        variant: {
            featured: '',
            default: '',
            editorial: '',
            editorialLarge: '',
        },
    },
    defaultVariants: {
        variant: 'featured',
    },
});

const _articleButtonVariants = cva('', {
    variants: {
        variant: {
            featured: '',
            default: 'hidden',
            editorial: 'hidden',
            editorialLarge: 'hidden',
        },
    },
});

/** The frame's own wording for a tile whose banner is a film (Figma 60:2086). */
const FILM_TILE_LABEL = 'Film Addition';

export const ArticleTile = (props: TileViewModel<ArticleTileViewModel>) => {
    const {
        aspectRatio,
        rounded = true,
        model: article,
        enableTileSlider,
        isFollowed,
        onFollow,
        routeState,
        hideFollowButton = false,
        followButton,
        overlay,
        inlineActionButtons,
        disableLink = false,
        onClick,
        className,
        contentEditable,
        section,
    } = props;
    const { variant = 'featured' } = article;
    const tracker = useTracker();
    const tileActions = useAtomValue(uiContextAtomTileActions);
    const tileRef = useRef<HTMLAnchorElement>(null);

    const handleTileClick = () => {
        onClick?.();
        if (disableLink) {
            return;
        }
        tileActions?.trackingConfig?.onClick?.(
            tileRef,
            article.id,
            article.professional.id,
            section,
        );
        trackTileMediaDetailClick(tracker, {
            itemID: article.id,
            itemType: 'ArticleTile',
            itemName: article.title,
        });
    };

    return (
        <OptionalLink
            ref={tileRef}
            mode={disableLink ? 'override' : 'normal'}
            to={article.link}
            prefetch="intent"
            state={routeState}
            onClick={handleTileClick}
        >
            <div className="h-full">
                <div className={cn('flex h-full flex-col gap-4', className)}>
                    <div className={_articleTileFrameVariants({ variant })}>
                        {variant === 'editorial' ||
                        variant === 'editorialLarge' ? (
                            <div
                                /*
                                 * A locked aspect, not a flexed one, and the
                                 * same one on both editorial variants.
                                 *
                                 * 437x524 is the only image box the design
                                 * states (60:2083). The category card's own
                                 * image is `flex: 1 0 0` inside a 622-tall
                                 * frame, so it has no ratio of its own — it
                                 * takes whatever the title leaves, which is
                                 * three lines in every mock. Measuring that
                                 * gives 432x478, a near-square that is an
                                 * artefact of the mock copy rather than a
                                 * decision, and with real titles it would give
                                 * every card a different image height and the
                                 * grid would stop reading as a grid.
                                 */
                                className="relative aspect-[437/524] w-full overflow-hidden"
                            >
                                <TileMedia
                                    aspectRatio={aspectRatio}
                                    rounded={rounded}
                                    images={[article.image]}
                                    video={article.video}
                                    enableTileSlider={enableTileSlider}
                                    gradiantOverlay={false}
                                />
                                {/*
                                 * Figma 60:2082. The glyph box is 38.59% of the
                                 * card's width, which is the width the frame's
                                 * triangle occupies once Rocco's play icon —
                                 * the same shape, to the same 13:15 — is scaled
                                 * to it. Sized off the card so it holds at both
                                 * editorial widths.
                                 */}
                                {article.hasVideo && (
                                    <div
                                        /*
                                         * The shadow is not in the frame,
                                         * whose card photo is mid-tone behind
                                         * the glyph. Real banner stills are
                                         * arbitrary and white on a bright sky
                                         * disappears; a shadow rescues those
                                         * without dimming the photo the way a
                                         * scrim would.
                                         */
                                        className="pointer-events-none absolute inset-0 [filter:drop-shadow(0_0_0.25rem_rgba(0,0,0,0.75))_drop-shadow(0_0_1.25rem_rgba(0,0,0,0.45))]"
                                    >
                                        <div
                                            aria-hidden={true}
                                            className="absolute inset-0 flex items-center justify-center"
                                        >
                                            <PlayIcon className="h-auto w-[38.59%] fill-white" />
                                        </div>
                                        <span
                                            className="absolute inset-x-0 text-center font-sans text-[1.2918rem] leading-[1.25] font-medium tracking-[-0.0323rem] text-white"
                                            style={{ top: '68.32%' }}
                                        >
                                            {FILM_TILE_LABEL}
                                        </span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <TileMedia
                                aspectRatio={aspectRatio}
                                rounded={rounded}
                                images={[article.image]}
                                video={article.video}
                                enableTileSlider={enableTileSlider}
                                gradiantOverlay={true}
                            />
                        )}
                        {overlay ? (
                            overlay
                        ) : (
                            <div className="absolute top-3.5 right-4 flex gap-1 md:invisible md:group-hover:visible">
                                {followButton
                                    ? followButton
                                    : !hideFollowButton && (
                                          <TileFollowButton
                                              pinId={article.id}
                                              pinType="ArticlePin"
                                              isFollowed={isFollowed}
                                              onFollow={onFollow}
                                          />
                                      )}
                            </div>
                        )}

                        {article.badge && (
                            <div className="absolute top-4 left-4">
                                <TileBadge title="Article" />
                            </div>
                        )}

                        <div
                            className={cn(
                                _articleTileContentVariants({ variant }),
                                TILE_COMPONENTS_CLASS_NAMES.TILE_DESCRIPTION_BLOCK,
                            )}
                        >
                            <div className="grid w-full grid-cols-[1fr_auto] items-end gap-2">
                                <div
                                    className={_articleTileTextStackVariants({
                                        variant,
                                    })}
                                >
                                    <span
                                        className={cn(
                                            _articleTileTitleVariants({
                                                variant,
                                            }),
                                            contentEditable?.enabled.title &&
                                                'focus-within:border-primary-500 -mx-2 cursor-text rounded-md border border-transparent px-2 focus-within:bg-gray-100 hover:border-gray-500',
                                        )}
                                        contentEditable={
                                            contentEditable?.enabled.title
                                        }
                                        role={
                                            contentEditable?.enabled.title
                                                ? 'textbox'
                                                : undefined
                                        }
                                        aria-label={
                                            contentEditable?.enabled.title
                                                ? 'Edit title'
                                                : undefined
                                        }
                                        aria-multiline="false"
                                        suppressContentEditableWarning={true}
                                        onInput={(
                                            e: React.FormEvent<HTMLSpanElement>,
                                        ) =>
                                            contentEditable?.onChange?.(
                                                (e.target as HTMLSpanElement)
                                                    .textContent ?? '',
                                                'title',
                                            )
                                        }
                                        onBlur={() => {
                                            contentEditable?.onBlur?.();
                                        }}
                                        onKeyDown={(
                                            e: React.KeyboardEvent<HTMLSpanElement>,
                                        ) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                (
                                                    e.target as HTMLSpanElement
                                                ).blur();
                                            }
                                        }}
                                    >
                                        {article.title}
                                    </span>
                                    <span
                                        className={cn(
                                            _articleTileMetaVariants({
                                                variant,
                                            }),
                                            contentEditable?.enabled
                                                .description &&
                                                'focus-within:border-primary-500 -ml-2 cursor-text rounded-md border border-transparent px-2 focus-within:bg-gray-100 hover:border-gray-500',
                                        )}
                                        contentEditable={
                                            contentEditable?.enabled.description
                                        }
                                        role={
                                            contentEditable?.enabled.description
                                                ? 'textbox'
                                                : undefined
                                        }
                                        aria-label={
                                            contentEditable?.enabled.description
                                                ? 'Edit description'
                                                : undefined
                                        }
                                        aria-multiline="false"
                                        suppressContentEditableWarning={true}
                                        onInput={(
                                            e: React.FormEvent<HTMLSpanElement>,
                                        ) =>
                                            contentEditable?.onChange?.(
                                                (e.target as HTMLSpanElement)
                                                    .textContent ?? '',
                                                'description',
                                            )
                                        }
                                        onBlur={() => {
                                            contentEditable?.onBlur?.();
                                        }}
                                        onKeyDown={(
                                            e: React.KeyboardEvent<HTMLSpanElement>,
                                        ) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                (
                                                    e.target as HTMLSpanElement
                                                ).blur();
                                            }
                                        }}
                                    >
                                        {variant === 'editorial' ||
                                        variant === 'editorialLarge'
                                            ? [
                                                  article.date &&
                                                      formatDateToDDMonthYYYY(
                                                          article.date,
                                                      ),
                                                  article.readTime,
                                              ]
                                                  .filter(Boolean)
                                                  .join(', ')
                                            : `${
                                                  article.date
                                                      ? `${formatDateToDDMMYY(article.date)} - `
                                                      : ''
                                              }Written by ${article.label}`}
                                    </span>
                                </div>
                                <div
                                    className={_articleButtonVariants({
                                        variant,
                                    })}
                                >
                                    <Button
                                        size="square42"
                                        color="primary"
                                        aria-label="Read article"
                                    >
                                        <ArrowRightIcon />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {inlineActionButtons && (
                    <div className="mt-4 flex flex-col">
                        {inlineActionButtons}
                    </div>
                )}
            </div>
        </OptionalLink>
    );
};
